/**
 * iOS Glassmorphism Design System
 *
 * Inspired by Apple's iOS design language featuring:
 * - Frosted glass effects with backdrop blur
 * - Subtle transparency and depth
 * - Vibrant colors with soft gradients
 * - Smooth animations and micro-interactions
 */

// =============================================================================
// COLOR PALETTE
// =============================================================================

export const glassColors = {
  // Primary Glass Surfaces
  glass: {
    // Standard glass - for cards, modals, panels
    standard: "rgba(255, 255, 255, 0.72)",
    // Thick glass - for important surfaces
    thick: "rgba(255, 255, 255, 0.85)",
    // Thin glass - for subtle overlays
    thin: "rgba(255, 255, 255, 0.45)",
    // Ultra thin - for backgrounds
    ultraThin: "rgba(255, 255, 255, 0.25)",
  },

  // Dark Glass (for dark mode or contrast)
  darkGlass: {
    standard: "rgba(30, 30, 30, 0.72)",
    thick: "rgba(30, 30, 30, 0.85)",
    thin: "rgba(30, 30, 30, 0.45)",
  },

  // iOS System Colors
  system: {
    blue: "#007AFF",
    green: "#34C759",
    indigo: "#5856D6",
    orange: "#FF9500",
    pink: "#FF2D55",
    purple: "#AF52DE",
    red: "#FF3B30",
    teal: "#5AC8FA",
    yellow: "#FFCC00",
  },

  // Labels (Text Colors)
  label: {
    primary: "rgba(0, 0, 0, 0.85)",
    secondary: "rgba(60, 60, 67, 0.60)",
    tertiary: "rgba(60, 60, 67, 0.30)",
    quaternary: "rgba(60, 60, 67, 0.18)",
  },

  // Fills (Background Colors)
  fill: {
    primary: "rgba(120, 120, 128, 0.20)",
    secondary: "rgba(120, 120, 128, 0.16)",
    tertiary: "rgba(118, 118, 128, 0.12)",
    quaternary: "rgba(116, 116, 128, 0.08)",
  },

  // Separators
  separator: {
    opaque: "rgba(198, 198, 200, 1)",
    nonOpaque: "rgba(60, 60, 67, 0.12)",
    ultraLight: "rgba(60, 60, 67, 0.06)",
  },

  // Backgrounds
  background: {
    primary: "#FFFFFF",
    secondary: "#F2F2F7",
    tertiary: "#FFFFFF",
    grouped: "#F2F2F7",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  // Shadows
  shadow: {
    small: "0 2px 8px rgba(0, 0, 0, 0.08)",
    medium: "0 4px 16px rgba(0, 0, 0, 0.12)",
    large: "0 8px 32px rgba(0, 0, 0, 0.16)",
    glow: (color) => `0 4px 20px ${color}40`,
  },
};

// =============================================================================
// BLUR EFFECTS
// =============================================================================

export const glassBlur = {
  // Standard blur for glass surfaces
  standard: {
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
  },
  // Light blur
  light: {
    backdropFilter: "blur(10px) saturate(150%)",
    WebkitBackdropFilter: "blur(10px) saturate(150%)",
  },
  // Heavy blur for important surfaces
  heavy: {
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
  },
  // No blur, just saturation
  none: {
    backdropFilter: "saturate(180%)",
    WebkitBackdropFilter: "saturate(180%)",
  },
};

// =============================================================================
// GLASS SURFACE MIXINS
// =============================================================================

export const glassSurface = {
  // Standard glass panel
  standard: {
    background: glassColors.glass.standard,
    ...glassBlur.standard,
    border: `0.5px solid rgba(255, 255, 255, 0.18)`,
    boxShadow: glassColors.shadow.small,
  },

  // Thick glass for important surfaces
  thick: {
    background: glassColors.glass.thick,
    ...glassBlur.heavy,
    border: `0.5px solid rgba(255, 255, 255, 0.25)`,
    boxShadow: glassColors.shadow.medium,
  },

  // Thin glass for overlays
  thin: {
    background: glassColors.glass.thin,
    ...glassBlur.light,
    border: `0.5px solid rgba(255, 255, 255, 0.12)`,
  },

  // Card style glass
  card: {
    background: glassColors.glass.standard,
    ...glassBlur.standard,
    borderRadius: 16,
    border: `0.5px solid rgba(255, 255, 255, 0.18)`,
    boxShadow: glassColors.shadow.small,
  },

  // Sidebar glass
  sidebar: {
    background: glassColors.glass.thick,
    ...glassBlur.heavy,
    borderRight: `0.5px solid ${glassColors.separator.nonOpaque}`,
  },

  // Modal glass
  modal: {
    background: glassColors.glass.thick,
    ...glassBlur.heavy,
    borderRadius: 20,
    border: `0.5px solid rgba(255, 255, 255, 0.25)`,
    boxShadow: glassColors.shadow.large,
  },

  // Floating bar glass
  floatingBar: {
    background: glassColors.glass.standard,
    ...glassBlur.standard,
    borderRadius: 12,
    border: `0.5px solid rgba(255, 255, 255, 0.18)`,
    boxShadow: glassColors.shadow.medium,
  },
};

// =============================================================================
// BUTTON STYLES
// =============================================================================

export const glassButton = {
  // Primary button (filled)
  primary: {
    background: glassColors.system.blue,
    color: "#FFFFFF",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    boxShadow: glassColors.shadow.glow(glassColors.system.blue),
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: `0 6px 24px ${glassColors.system.blue}50`,
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  },

  // Secondary button (glass)
  secondary: {
    background: glassColors.glass.thin,
    ...glassBlur.light,
    color: glassColors.system.blue,
    border: `1px solid ${glassColors.system.blue}30`,
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: "0.875rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: `${glassColors.system.blue}15`,
      borderColor: `${glassColors.system.blue}50`,
    },
  },

  // Ghost button (minimal)
  ghost: {
    background: "transparent",
    color: glassColors.system.blue,
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: glassColors.fill.quaternary,
    },
  },

  // Icon button
  icon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: glassColors.label.secondary,
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: glassColors.fill.tertiary,
      color: glassColors.label.primary,
    },
  },

  // Pill button (tag style)
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 20,
    background: glassColors.fill.tertiary,
    color: glassColors.label.primary,
    fontSize: "0.75rem",
    fontWeight: 500,
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: glassColors.fill.secondary,
    },
  },
};

// =============================================================================
// INPUT STYLES
// =============================================================================

export const glassInput = {
  // Standard text input
  standard: {
    background: glassColors.fill.quaternary,
    border: `1px solid transparent`,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: "0.9375rem",
    color: glassColors.label.primary,
    transition: "all 0.2s ease",
    outline: "none",
    "&::placeholder": {
      color: glassColors.label.tertiary,
    },
    "&:focus": {
      background: glassColors.glass.standard,
      border: `1px solid ${glassColors.system.blue}`,
      boxShadow: `0 0 0 4px ${glassColors.system.blue}15`,
    },
  },

  // Search input
  search: {
    background: glassColors.fill.tertiary,
    border: "none",
    borderRadius: 10,
    padding: "10px 16px 10px 40px",
    fontSize: "0.875rem",
    color: glassColors.label.primary,
    transition: "all 0.2s ease",
    outline: "none",
    "&::placeholder": {
      color: glassColors.label.tertiary,
    },
    "&:focus": {
      background: glassColors.fill.secondary,
    },
  },
};

// =============================================================================
// CHIP/TAG STYLES
// =============================================================================

export const glassChip = {
  // Standard chip
  standard: (color = glassColors.system.blue) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 6,
    background: `${color}12`,
    color: color,
    fontSize: "0.6875rem",
    fontWeight: 600,
    border: `1px solid ${color}25`,
    transition: "all 0.2s ease",
    "&:hover": {
      background: `${color}20`,
      transform: "translateY(-1px)",
    },
  }),

  // Status chip
  status: {
    pending: {
      background: `${glassColors.system.orange}12`,
      color: glassColors.system.orange,
      border: `1px solid ${glassColors.system.orange}25`,
    },
    completed: {
      background: `${glassColors.system.green}12`,
      color: glassColors.system.green,
      border: `1px solid ${glassColors.system.green}25`,
    },
    inProgress: {
      background: `${glassColors.system.blue}12`,
      color: glassColors.system.blue,
      border: `1px solid ${glassColors.system.blue}25`,
    },
    error: {
      background: `${glassColors.system.red}12`,
      color: glassColors.system.red,
      border: `1px solid ${glassColors.system.red}25`,
    },
  },
};

// =============================================================================
// ANIMATION PRESETS
// =============================================================================

export const glassAnimations = {
  // Smooth spring-like transition
  spring: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  // Quick snappy transition
  snappy: "all 0.15s ease-out",
  // Gentle fade
  fade: "opacity 0.2s ease",
  // Bounce effect
  bounce: "all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
};

// =============================================================================
// TAB STYLES
// =============================================================================

export const glassTabs = {
  container: {
    background: "transparent",
    minHeight: 44,
  },
  indicator: {
    height: 3,
    borderRadius: 1.5,
    backgroundColor: glassColors.system.blue,
    boxShadow: `0 0 12px ${glassColors.system.blue}60`,
  },
  tab: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    minHeight: 44,
    padding: "8px 16px",
    color: glassColors.label.secondary,
    borderRadius: 8,
    margin: "0 2px",
    transition: glassAnimations.snappy,
    "&:hover": {
      background: glassColors.fill.quaternary,
      color: glassColors.label.primary,
    },
    "&.Mui-selected": {
      color: glassColors.system.blue,
      fontWeight: 600,
    },
  },
};

// =============================================================================
// LIST STYLES
// =============================================================================

export const glassList = {
  container: {
    background: "transparent",
    padding: 0,
    margin: 0,
    listStyle: "none",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderRadius: 12,
    cursor: "pointer",
    transition: glassAnimations.snappy,
    "&:hover": {
      background: glassColors.fill.quaternary,
    },
  },
  itemActive: {
    background: `${glassColors.system.blue}12`,
    color: glassColors.system.blue,
    fontWeight: 600,
  },
  itemIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: glassColors.fill.tertiary,
  },
  itemText: {
    flex: 1,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: glassColors.label.primary,
  },
  itemBadge: {
    fontSize: "0.6875rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 10,
    background: glassColors.fill.tertiary,
    color: glassColors.label.secondary,
  },
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const glassTypography = {
  h1: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: glassColors.label.primary,
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
  },
  h2: {
    fontSize: "1.375rem",
    fontWeight: 600,
    color: glassColors.label.primary,
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
  },
  h3: {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: glassColors.label.primary,
    letterSpacing: "-0.01em",
    lineHeight: 1.4,
  },
  body: {
    fontSize: "0.9375rem",
    fontWeight: 400,
    color: glassColors.label.primary,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: glassColors.label.secondary,
    lineHeight: 1.4,
  },
  label: {
    fontSize: "0.6875rem",
    fontWeight: 600,
    color: glassColors.label.tertiary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
};

// =============================================================================
// SCROLLBAR STYLES
// =============================================================================

export const glassScrollbar = {
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: glassColors.fill.secondary,
    borderRadius: 4,
    "&:hover": {
      background: glassColors.fill.primary,
    },
  },
};

// =============================================================================
// EXPORT ALL
// =============================================================================

export default {
  colors: glassColors,
  blur: glassBlur,
  surface: glassSurface,
  button: glassButton,
  input: glassInput,
  chip: glassChip,
  animations: glassAnimations,
  tabs: glassTabs,
  list: glassList,
  typography: glassTypography,
  scrollbar: glassScrollbar,
};
